import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Spinner, Table, Button, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS (Modern SIMRS Ultra-Compact UI)
────────────────────────────────────────────────────────────── */
const T = {
  primary:     '#0d9488', // Teal
  primaryDk:   '#0f766e',
  primaryLt:   '#ccfbf1',
  topHeaderBg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  headerBg:    '#f8fafc',
  headerBgSub: '#f1f5f9',
  headerText:  '#1e293b',
  headerBorder:'#cbd5e1',
  danger:      '#dc2626',
  dangerDk:    '#b91c1c',
  warn:        '#d97706',
  info:        '#0284c7',
  infoLt:      '#e0f2fe',
  slate50:     '#f8fafc',
  slate100:    '#f1f5f9',
  slate200:    '#e2e8f0',
  slate300:    '#cbd5e1',
  slate500:    '#64748b',
  slate600:    '#475569',
  slate700:    '#334155',
  slate800:    '#1e293b',
  white:       '#ffffff',
  border:      '#cbd5e1',
  navy:        '#0f172a',
  blue:        '#1e3a5f',
  blueHeader:  '#003366',
  blueBar:     '#004080',
};

const fmtRp = (v) => {
  const num = parseFloat(v) || 0;
  return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

const S = {
  input: {
    border: `1px solid ${T.border}`, borderRadius: '3px',
    fontSize: '0.73rem', padding: '1px 6px', outline: 'none',
    background: T.white, color: T.slate800, height: '24px',
    transition: 'border-color .15s, box-shadow .15s',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDk} 100%)`,
    border: 'none', borderRadius: '4px', color: T.white, fontWeight: 700,
    fontSize: '0.71rem', padding: '2px 10px', cursor: 'pointer', height: '24px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    transition: 'opacity .15s',
  },
  btnDanger: {
    background: `linear-gradient(135deg, ${T.danger} 0%, ${T.dangerDk} 100%)`,
    border: 'none', borderRadius: '4px', color: T.white, fontWeight: 700,
    fontSize: '0.71rem', padding: '2px 10px', cursor: 'pointer', height: '24px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  btnGhost: {
    background: 'transparent', border: `1px solid ${T.border}`, borderRadius: '4px',
    color: T.slate600, fontWeight: 600, fontSize: '0.7rem', padding: '1px 7px', height: '22px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px',
  },
  card: {
    background: T.white, borderRadius: '4px',
    border: `1px solid ${T.border}`, boxShadow: '0 1px 2px rgba(0,0,0,.04)',
  },
  dlgLabel: {
    background: '#004080', color: T.white, padding: '2px 8px',
    fontSize: '0.68rem', fontWeight: 700, borderRadius: '2px', letterSpacing: '0.2px',
    display: 'flex', alignItems: 'center', height: '23px', whiteSpace: 'nowrap', overflow: 'hidden'
  },
  dlgInput: {
    border: `1px solid #94a3b8`, borderRadius: '2px',
    fontSize: '0.71rem', padding: '1px 6px', outline: 'none',
    background: T.white, color: T.slate800, height: '23px', width: '100%'
  },
  dlgBtn: {
    background: 'linear-gradient(180deg, #f8fafc 0%, #cbd5e1 100%)',
    border: '1px solid #94a3b8', borderRadius: '3px',
    color: '#0f172a', fontWeight: 700, fontSize: '0.7rem',
    padding: '2px 8px', height: '26px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap'
  }
};

const ValidasiRekananPerusahaan = () => {
  const navigate = useNavigate();
  const getApiUrl = () => localStorage.getItem('api_url') || 'http://localhost:8000';
  const getToken = () => localStorage.getItem('token');

  // Filter Form States (Sesuai VB cmbbyr & tanggal validasi periode1 s/d periode2)
  const [cmbbyr, setCmbbyr] = useState('0'); // 0=Piutang, 1=Tunai
  
  const todayStr = new Date().toISOString().split('T')[0];
  const [periode1, setPeriode1] = useState(todayStr);
  const [periode2, setPeriode2] = useState(todayStr);
  const [semuaTgl, setSemuaTgl] = useState(false);

  // Search States (Sesuai VB txtrmcari & txtnmrmcari)
  const [txtrmcari, setTxtrmcari] = useState('');
  const [txtnmrmcari, setTxtnmrmcari] = useState('');

  // Table Data & Active Row Selection
  const [listData, setListData] = useState([]);
  const [selectedNotas, setSelectedNotas] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTable, setSearchTable] = useState('');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Clock
  const [clock, setClock] = useState({ date: '', time: '' });

  // Dialog "VALIDASI DATA JAMINAN KUITANSI DAN KELENGKAPAN DATA PERUJUK" States
  const [showCetakModal, setShowCetakModal] = useState(false);
  const [dlgJenisValidasi, setDlgJenisValidasi] = useState('Tunai');
  const [dlgNoKuitansi, setDlgNoKuitansi] = useState('');
  const [dlgTglKuitansi, setDlgTglKuitansi] = useState('');
  const [dlgNilai, setDlgNilai] = useState(0);
  const [dlgNoMedrec, setDlgNoMedrec] = useState('');
  const [dlgNmPasien, setDlgNmPasien] = useState('');
  const [dlgTipeJaminan, setDlgTipeJaminan] = useState('1. External Perusahaan Rekanan');
  const [dlgKdPrs, setDlgKdPrs] = useState('');
  const [dlgNmPrs, setDlgNmPrs] = useState('');
  const [dlgKdIcd, setDlgKdIcd] = useState('');
  const [dlgNmIcd, setDlgNmIcd] = useState('');
  const [dlgTindakan, setDlgTindakan] = useState('');
  const [dlgKdPerujuk, setDlgKdPerujuk] = useState('');
  const [dlgNmPerujuk, setDlgNmPerujuk] = useState('');
  const [dlgKdDokter, setDlgKdDokter] = useState('');
  const [dlgNmDokter, setDlgNmDokter] = useState('');
  const [dlgNmPenjamin, setDlgNmPenjamin] = useState('');
  const [dlgTempatBekerja, setDlgTempatBekerja] = useState('');
  const [dlgNoSep, setDlgNoSep] = useState('');
  const [dlgKelasRawat, setDlgKelasRawat] = useState('');
  const [dlgKdPaket, setDlgKdPaket] = useState('');
  const [dlgNilaiRujukan, setDlgNilaiRujukan] = useState('');
  const [dlgNoRincian, setDlgNoRincian] = useState('');
  const [dlgKdKlasifikasi, setDlgKdKlasifikasi] = useState('');
  const [dlgKlasifikasi, setDlgKlasifikasi] = useState('');
  const [dlgAsal, setDlgAsal] = useState('');
  const [dlgNmKamar, setDlgNmKamar] = useState('');
  const [isLoadingCek, setIsLoadingCek] = useState(false);

  // PDF Preview Modal States (Hasil Lab & Hasil Rad)
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfModalTitle, setPdfModalTitle] = useState('');
  const [pdfDownloadName, setPdfDownloadName] = useState('Dokumen.pdf');

  // Modal Konfirmasi & Progress Bar Cetak Hasil Lab
  const [showConfirmLabModal, setShowConfirmLabModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressStatusText, setProgressStatusText] = useState('');

  // Modal Konfirmasi Cetak Ulang Validasi Jaminan
  const [showConfirmCetakUlangModal, setShowConfirmCetakUlangModal] = useState(false);
  const [showCetakCariDataModal, setShowCetakCariDataModal] = useState(false);
  const [cetakCariType, setCetakCariType] = useState('1');
  const [cetakCariTgl1, setCetakCariTgl1] = useState(new Date().toISOString().split('T')[0]);
  const [cetakCariTgl2, setCetakCariTgl2] = useState(new Date().toISOString().split('T')[0]);

  // Toast
  const [toast, setToast] = useState({ show: false, type: 'success', msg: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  // Clock ticker
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClock({
        date: now.toLocaleDateString('id-ID', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
        time: now.toLocaleTimeString('id-ID'),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch Data Validasi from Backend (/show_validasirekananperusahaan)
  const fetchValidasi = async () => {
    setIsLoading(true);
    setSelectedNotas([]);
    setCurrentPage(1);
    try {
      const res = await fetch(`${getApiUrl()}/show_validasirekananperusahaan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          cmbbyr: cmbbyr,
          periode1: periode1,
          periode2: periode2,
          semua_tgl: semuaTgl,
          txtrmcari: txtrmcari.trim(),
          txtnmrmcari: txtnmrmcari.trim(),
        })
      });
      const data = await res.json();
      if (data.status === 1 || Array.isArray(data.list_data)) {
        const rows = data.list_data || [];
        setListData(rows);
        if (rows.length > 0) {
          setActiveRow(rows[0]);
        } else {
          setActiveRow(null);
          showToast(`Tidak ada data validasi untuk periode ini`, 'warning');
        }
      } else {
        showToast(data.msg || 'Gagal memuat data validasi.', 'danger');
        setListData([]);
        setActiveRow(null);
      }
    } catch (e) {
      showToast('Error memuat data: ' + e.message, 'danger');
      setListData([]);
      setActiveRow(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto reload when cmbbyr or date changes
  useEffect(() => {
    fetchValidasi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmbbyr, periode1, periode2, semuaTgl]);

  // Reset page when search or itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTable, itemsPerPage]);

  // Filtered rows by live table search
  const filteredData = useMemo(() => {
    if (!searchTable) return listData;
    const q = searchTable.toLowerCase();
    return listData.filter(item =>
      (item.no_kuitansi || '').toLowerCase().includes(q) ||
      (item.no_nota || '').toLowerCase().includes(q) ||
      (item.nm_pasien || '').toLowerCase().includes(q) ||
      (item.no_medrec || '').toLowerCase().includes(q) ||
      (item.nm_dokter || '').toLowerCase().includes(q) ||
      (item.kasir || '').toLowerCase().includes(q)
    );
  }, [listData, searchTable]);

  // Pagination slicing
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  // Handle Select All / Toggle Rows
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNotas(filteredData.map(item => item.no_kuitansi || item.no_nota));
    } else {
      setSelectedNotas([]);
    }
  };

  const toggleRowSelect = (key, rowObj) => {
    if (rowObj) setActiveRow(rowObj);
    setSelectedNotas(prev =>
      prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]
    );
  };

  // Calculations: Total sum of total column
  const totalSum = useMemo(() => {
    return listData.reduce((acc, cur) => acc + (parseFloat(cur.total) || 0), 0);
  }, [listData]);

  // Function cekdatapiutang (dipanggil saat Enter di Nomor Kuitansi pada dialog)
  const cekdatapiutang = async (nokwt = null, jenisOverride = null) => {
    const kuitansiQuery = (nokwt || dlgNoKuitansi || '').trim();
    if (!kuitansiQuery) {
      showToast('Masukkan nomor kuitansi terlebih dahulu!', 'warning');
      return;
    }

    const currentJenis = jenisOverride !== null ? jenisOverride : (dlgJenisValidasi === 'Tunai' ? '1' : '0');

    setIsLoadingCek(true);
    try {
      const res = await fetch(`${getApiUrl()}/cekdatapiutang`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          cmbjenis: currentJenis,
          no_kuitansi: kuitansiQuery
        })
      });
      const resData = await res.json();
      if (resData.status === 1 && resData.data) {
        const d = resData.data;
        setDlgNoKuitansi(d.no_kuitansi || kuitansiQuery);
        setDlgTglKuitansi(d.tgl_pulang || '');
        setDlgNilai(d.total || 0);
        setDlgNoMedrec(d.no_medrec || '');
        setDlgNmPasien(d.nm_pasien || '');

        // Tipe Jaminan
        if (d.type_jaminan === '4' || currentJenis === '1') {
          setDlgTipeJaminan('Tunai');
        } else if (d.type_jaminan === '1') {
          setDlgTipeJaminan('1. External Perusahaan Rekanan');
        } else if (d.type_jaminan === '2') {
          setDlgTipeJaminan('2. Internal Dokter Rumah Sakit');
        } else if (d.type_jaminan === '3') {
          setDlgTipeJaminan('3. Internal Pegawai');
        } else {
          setDlgTipeJaminan(d.type_jaminan || '1. External Perusahaan Rekanan');
        }

        setDlgKdPrs(d.kd_prs || '');
        setDlgNmPrs(d.nm_prs || d.perusahaan || '');
        setDlgKdIcd(d.kd_icd || '');
        setDlgNmIcd(d.diagnosa || d.nm_icd || '');
        setDlgTindakan(d.tindakan || '-');
        setDlgKdPerujuk(d.kd_perujuk || '');
        setDlgNmPerujuk(d.nm_perujuk || '');
        setDlgKdDokter(d.kd_dokter || '');
        setDlgNmDokter(d.nm_dokter || '');
        setDlgNmPenjamin(d.penjamin || d.nm_pasien || '');
        setDlgTempatBekerja(d.perusahaan || d.nm_wilayah || '');
        setDlgNoSep(d.no_sep || d.nrp || '');
        setDlgKelasRawat(d.nm_klas || d.kd_klas || '');
        setDlgKdPaket(d.kd_paket || '');
        setDlgNilaiRujukan(d.rujukan || '');
        setDlgNoRincian(d.no_nota || d.no_rincian || '');
        setDlgKdKlasifikasi(d.kd_klasifikasi || '');
        setDlgKlasifikasi(d.nm_klasifikasi || '');
        setDlgAsal(d.asal || '');
        setDlgNmKamar(d.no_kamar || '');

        showToast(resData.msg || 'Data kuitansi berhasil dimuat!', 'success');
      } else if (resData.status === 2) {
        showToast(resData.msg || 'Data tidak perlu diperiksa ...', 'warning');
      } else {
        showToast(resData.msg || 'Nomor Kuitansi tidak terdaftar !', 'danger');
      }
    } catch (e) {
      showToast('Error cek data piutang: ' + e.message, 'danger');
    } finally {
      setIsLoadingCek(false);
    }
  };

  // Cetak Hasil Laboratorium All (cetakhasillab_all) - Trigger Confirmation Modal
  const handleCetakHasilLab = () => {
    const nokwt = (dlgNoKuitansi || '').trim();
    if (!nokwt) {
      showToast('Nomor kuitansi belum diisi / tidak dipilih!', 'warning');
      return;
    }
    setShowConfirmLabModal(true);
  };

  // Execute Cetak Hasil Laboratorium with Progress Bar
  const executeCetakHasilLab = async () => {
    setShowConfirmLabModal(false);
    const nokwt = (dlgNoKuitansi || '').trim();
    if (!nokwt) return;

    setShowProgressModal(true);
    setProgressPercent(10);
    setProgressStatusText('Menghubungi server laboratorium...');

    let pVal = 10;
    const timer = setInterval(() => {
      pVal = Math.min(pVal + 15, 85);
      setProgressPercent(pVal);
      if (pVal > 30 && pVal <= 65) {
        setProgressStatusText('Mengambil rincian nota & hasil pemeriksaan...');
      } else if (pVal > 65) {
        setProgressStatusText('Menyusun dokumen cetak laboratorium...');
      }
    }, 150);

    try {
      const res = await fetch(`${getApiUrl()}/cetakhasillab_all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ nokuitansi: nokwt })
      });

      clearInterval(timer);

      if (!res.ok) {
        setProgressPercent(100);
        setTimeout(() => setShowProgressModal(false), 250);
        try {
          const errData = await res.json();
          if (errData.status === 0 || errData.status === '0') {
            showToast('Tidak ada transaksi Laboratorium di kuitansi ini', 'warning');
          } else {
            showToast(errData.msg || 'Tidak ada transaksi Laboratorium di kuitansi ini', 'warning');
          }
        } catch (_) {
          showToast('Tidak ada transaksi Laboratorium di kuitansi ini', 'warning');
        }
        return;
      }

      const blob = await res.blob();
      if (blob.size < 250 && (blob.type.includes('json') || blob.type.includes('text'))) {
        setProgressPercent(100);
        setTimeout(() => setShowProgressModal(false), 250);
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json.status === 0 || json.status === '0') {
            showToast('Tidak ada transaksi Laboratorium di kuitansi ini', 'warning');
          } else {
            showToast(json.msg || 'Tidak ada transaksi Laboratorium di kuitansi ini', 'warning');
          }
          return;
        } catch (_) {}
      }

      setProgressPercent(100);
      setProgressStatusText('Dokumen siap!');
      const objectUrl = URL.createObjectURL(blob);
      setPdfPreviewUrl(objectUrl);
      setPdfModalTitle(`HASIL LABORATORIUM — KUITANSI: ${nokwt}`);
      setPdfDownloadName(`HasilLab_${nokwt.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      setTimeout(() => {
        setShowProgressModal(false);
        setShowPdfModal(true);
      }, 300);
    } catch (err) {
      clearInterval(timer);
      setProgressPercent(100);
      setTimeout(() => setShowProgressModal(false), 250);
      showToast(err.message || 'Terjadi kesalahan saat memproses PDF Hasil Lab.', 'danger');
    }
  };

  // Cetak Hasil Radiologi Gabungan (cetakhasilrad_gab)
  const handleCetakHasilRad = async () => {
    const nokwt = (dlgNoKuitansi || '').trim();
    if (!nokwt) {
      showToast('Nomor kuitansi belum diisi / tidak dipilih!', 'warning');
      return;
    }

    try {
      const res = await fetch(`${getApiUrl()}/cetakhasilrad_gab`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ nokuitansi: nokwt })
      });

      if (!res.ok) {
        try {
          const errData = await res.json();
          if (errData.status === 0 || errData.status === '0') {
            showToast('Tidak ada transaksi Radiologi di kuitansi ini', 'warning');
          } else {
            showToast(errData.msg || 'Tidak ada transaksi Radiologi di kuitansi ini', 'warning');
          }
        } catch (_) {
          showToast('Tidak ada transaksi Radiologi di kuitansi ini', 'warning');
        }
        return;
      }

      const blob = await res.blob();
      if (blob.size < 250 && (blob.type.includes('json') || blob.type.includes('text'))) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json.status === 0 || json.status === '0') {
            showToast('Tidak ada transaksi Radiologi di kuitansi ini', 'warning');
          } else {
            showToast(json.msg || 'Tidak ada transaksi Radiologi di kuitansi ini', 'warning');
          }
          return;
        } catch (_) {}
      }

      const objectUrl = URL.createObjectURL(blob);
      setPdfPreviewUrl(objectUrl);
      setPdfModalTitle(`HASIL RADIOLOGI (GABUNGAN) — KUITANSI: ${nokwt}`);
      setPdfDownloadName(`HasilRad_${nokwt.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      setShowPdfModal(true);
    } catch (err) {
      showToast(err.message || 'Terjadi kesalahan saat memproses PDF Hasil Radiologi.', 'danger');
    }
  };

  // Handle Cetak Ulang Button Click (Validation & Confirm Trigger: btnsimpan1_Click)
  const handleCetakUlangJaminan = () => {
    const kprs = (dlgKdPrs || '').trim();
    const nprs = (dlgNmPrs || '').trim();
    const nkwt = (dlgNoKuitansi || '').trim();
    const tj = (dlgTipeJaminan || '').trim();

    if (!kprs || !nprs || !nkwt || tj === '' || tj === '0') {
      showToast('Isian masih belum lengkap ...', 'warning');
      return;
    }
    setShowConfirmCetakUlangModal(true);
  };

  // Execute Cetak Ulang Jaminan
  const executeCetakUlangJaminan = async () => {
    setShowConfirmCetakUlangModal(false);
    setShowProgressModal(true);
    setProgressPercent(15);
    setProgressStatusText('Memeriksa kelengkapan hasil laboratorium...');

    let pVal = 15;
    const timer = setInterval(() => {
      pVal = Math.min(pVal + 15, 85);
      setProgressPercent(pVal);
      if (pVal > 30 && pVal <= 65) {
        setProgressStatusText('Menghitung rincian tagihan & verifikasi paket...');
      } else if (pVal > 65) {
        setProgressStatusText('Menyusun lembar cetak kuitansi & rincian...');
      }
    }, 150);

    try {
      const res = await fetch(`${getApiUrl()}/cetakulang_validasi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          kd_prs: dlgKdPrs,
          nm_prs: dlgNmPrs,
          cmbtypejamin: dlgTipeJaminan,
          no_kuitansi: dlgNoKuitansi,
          no_rincian: dlgNoRincian || dlgNoKuitansi,
          no_medrec: dlgNoMedrec,
          nm_pasien: dlgNmPasien,
          nm_dokter: dlgNmDokter,
          nm_klas: dlgKelasRawat,
          nm_klasifikasi: dlgKlasifikasi,
          kd_klasifikasi: dlgKdKlasifikasi,
          tgl_pulang: dlgTglKuitansi,
          total: dlgNilai,
          username: sessionStorage.getItem('username') || localStorage.getItem('username') || 'Petugas',
          asal: dlgAsal,
          diagnosa: dlgNmIcd,
          tindakan: dlgTindakan,
          nm_kamar: dlgNmKamar,
          no_sep: dlgNoSep,
          perusahaan: dlgNmPenjamin
        })
      });

      clearInterval(timer);
      setProgressPercent(100);
      setTimeout(() => setShowProgressModal(false), 250);

      if (!res.ok) {
        try {
          const errData = await res.json();
          showToast(errData.msg || 'Dokumen pelengkap untuk penagihan belum cukup !', 'warning');
        } catch (_) {
          showToast(`Gagal memproses cetak ulang (${res.status})`, 'warning');
        }
        return;
      }

      const blob = await res.blob();
      if (blob.size < 250 && (blob.type.includes('json') || blob.type.includes('text'))) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          showToast(json.msg || 'Dokumen pelengkap untuk penagihan belum cukup !', 'warning');
          return;
        } catch (_) {}
      }

      const objectUrl = URL.createObjectURL(blob);
      setPdfPreviewUrl(objectUrl);
      setPdfModalTitle(`KUITANSI & RINCIAN BIAYA TAGIHAN — ${dlgNoKuitansi}`);
      setPdfDownloadName(`Kuitansi_Rincian_${(dlgNoKuitansi || 'kwt').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      setShowPdfModal(true);
      showToast('Dokumen PDF Kuitansi & Rincian berhasil dibuat!', 'success');
    } catch (err) {
      clearInterval(timer);
      setProgressPercent(100);
      setTimeout(() => setShowProgressModal(false), 250);
      showToast('Terjadi kesalahan saat memproses cetak ulang: ' + err.message, 'danger');
    }
  };

  const handleCetakLaporan = async () => {
    setShowCetakCariDataModal(false);
    setShowProgressModal(true);
    setProgressPercent(10);
    setProgressStatusText('Menyiapkan parameter pencarian...');

    let pVal = 10;
    const timer = setInterval(() => {
      pVal = Math.min(pVal + 10, 85);
      setProgressPercent(pVal);
      if (pVal > 30 && pVal <= 60) {
        setProgressStatusText('Mengambil rekapan data transaksi...');
      } else if (pVal > 60) {
        setProgressStatusText('Menyusun file dokumen Excel...');
      }
    }, 200);

    try {
      let endpoint = '';
      let payload = {};

      if (cetakCariType === '1') {
        endpoint = '/laporan_rekap_otorisasi_transaksi';
        payload = {
          periode1: cetakCariTgl1,
          periode2: cetakCariTgl2,
          format: 'excel'
        };
      } else {
        endpoint = '/laporan_piutang_belum_valid_tagih';
        payload = {
          format: 'excel'
        };
      }

      const res = await fetch(`${getApiUrl()}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      clearInterval(timer);
      setProgressPercent(100);
      setProgressStatusText('Laporan selesai dibuat!');

      setTimeout(() => setShowProgressModal(false), 300);

      if (data.status === 1 && data.download_url) {
        showToast(data.msg || 'Laporan berhasil dibuat', 'success');
        const fileUrl = getApiUrl() + data.download_url;
        if (data.download_url.toLowerCase().endsWith('.pdf')) {
          setPdfPreviewUrl(fileUrl);
          setPdfModalTitle(cetakCariType === '1' ? 'Rekapitulasi Otorisasi Transaksi' : 'Laporan Piutang Belum Valid Tagih');
          setPdfDownloadName(data.download_url.split('/').pop() || 'Laporan.pdf');
          setShowPdfModal(true);
        } else {
          // Direct download tanpa membuka tab browser baru
          const link = document.createElement('a');
          link.href = fileUrl;
          link.setAttribute('download', data.download_url.split('/').pop() || 'Export');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        showToast(data.msg || 'Gagal membuat laporan', 'danger');
      }
    } catch (err) {
      clearInterval(timer);
      setShowProgressModal(false);
      showToast('Terjadi kesalahan koneksi saat memproses laporan', 'danger');
      console.error(err);
    }
  };

  // Open Cetak Ulang Dialog Window
  const handleOpenCetakModal = (specificRow = null) => {
    const target = specificRow || activeRow || (listData.length > 0 ? listData[0] : null);
    if (!target && listData.length === 0) {
      showToast('Tidak ada data yang dipilih untuk dicetak!', 'warning');
      return;
    }
    if (target) setActiveRow(target);

    const t = target || {};
    const jns = cmbbyr === '1' ? 'Tunai' : 'Piutang';
    setDlgJenisValidasi(jns);
    const nokwt = t.no_kuitansi || t.no_nota || '';
    setDlgNoKuitansi(nokwt);
    setDlgNoRincian(t.no_nota || '');
    setDlgTglKuitansi(t.tgl_pulang || periode1 || '');
    setDlgNilai(t.total || 0);
    setDlgNoMedrec(t.no_medrec || '');
    setDlgNmPasien(t.nm_pasien || '');
    setDlgTipeJaminan(cmbbyr === '1' ? 'Tunai' : '1. External Perusahaan Rekanan');
    setDlgKdPrs(t.kd_prs || '');
    setDlgNmPrs(t.nm_prs || '');
    setDlgKdIcd('');
    setDlgNmIcd('');
    setDlgTindakan('');
    setDlgKdPerujuk('');
    setDlgNmPerujuk('');
    setDlgKdDokter('');
    setDlgNmDokter(t.nm_dokter || '');
    setDlgNmPenjamin(t.nm_prs || '');
    setDlgTempatBekerja('');
    setDlgNoSep('');
    setDlgKelasRawat(t.kd_klasifikasi || '');
    setDlgKdPaket('');
    setDlgNilaiRujukan('');
    setDlgKdKlasifikasi(t.kd_klasifikasi || '');
    setDlgKlasifikasi(t.nm_klasifikasi || '');
    setDlgAsal(t.asal || '');
    setDlgNmKamar(t.no_kamar || '');

    setShowCetakModal(true);

    if (nokwt) {
      cekdatapiutang(nokwt, cmbbyr === '1' ? '1' : '0');
    }
  };

  // Pagination page buttons generator
  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          style={{
            ...S.btnGhost,
            padding: '1px 6px', height: '22px', fontSize: '0.7rem', minWidth: '22px',
            background: currentPage === i ? T.primary : T.white,
            color: currentPage === i ? T.white : T.slate700,
            borderColor: currentPage === i ? T.primary : T.border,
            fontWeight: currentPage === i ? 800 : 600
          }}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div style={{
      background: T.slate100, height: '100vh', maxHeight: '100vh', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Segoe UI',sans-serif"
    }}>

      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '8px', right: '12px', zIndex: 9999,
          background: toast.type === 'success' ? '#022c22' : toast.type === 'warning' ? '#451a03' : '#450a0a',
          color: T.white, borderRadius: '4px', padding: '6px 12px',
          boxShadow: '0 4px 12px rgba(0,0,0,.2)',
          display: 'flex', alignItems: 'center', gap: '6px', minWidth: '240px',
          borderLeft: `3px solid ${toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444'}`,
          animation: 'slideIn .2s ease',
        }}>
          <i className={`bi ${toast.type === 'success' ? 'bi-check-circle-fill' : toast.type === 'warning' ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'}`}
            style={{ fontSize: '0.85rem', color: toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444' }} />
          <span style={{ fontSize: '0.74rem', fontWeight: 600 }}>{toast.msg}</span>
        </div>
      )}

      {/* TOPBAR HEADER (SIMRS THEME) */}
      <div style={{ background: T.topHeaderBg, borderBottom: `1.5px solid ${T.primaryDk}`, boxShadow: '0 1px 3px rgba(13, 148, 136, 0.2)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '34px', padding: '0 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '22px', height: '22px', borderRadius: '4px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-shield-check" style={{ fontSize: '0.8rem', color: T.white }} />
            </div>
            <div>
              <span style={{ color: T.white, fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.3px' }}>
                LAPORAN VERIFIKASI KASIR — VALIDASI REKANAN &amp; PERUSAHAAN
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#ccfbf1', fontSize: '0.66rem', fontWeight: 600 }}>{clock.date}</span>
              <span style={{ color: T.white, fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace' }}>{clock.time} WIB</span>
            </div>
            <button
              onClick={() => navigate('/home')}
              style={{ ...S.btnGhost, height: '22px', padding: '1px 6px', fontSize: '0.68rem', borderColor: 'rgba(255,255,255,0.4)', color: T.white, background: 'rgba(255,255,255,0.15)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            >
              <i className="bi bi-arrow-left" /> Kembali
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div style={{ padding: '4px 8px', flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minHeight: 0 }}>

        {/* ── TOP FORM FILTER (REKAPITULASI) ── */}
        <div style={{
          ...S.card,
          background: T.white,
          border: `1px solid ${T.border}`,
          overflow: 'visible',
          flexShrink: 0
        }}>
          {/* Header Title Bar */}
          <div style={{
            background: 'linear-gradient(90deg, #003366 0%, #004080 100%)',
            color: T.white, padding: '3px 8px',
            textAlign: 'center', fontWeight: 800, fontSize: '0.74rem', letterSpacing: '0.6px',
            borderRadius: '3px 3px 0 0'
          }}>
            REKAPITULASI
          </div>

          <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>

            {/* Row 1: JENIS VALIDASI & TANGGAL VALIDASI */}
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 1fr', alignItems: 'center', gap: '6px' }}>
              
              {/* JENIS VALIDASI */}
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                JENIS VALIDASI
              </div>
              <div>
                <select
                  value={cmbbyr}
                  onChange={e => setCmbbyr(e.target.value)}
                  style={{ ...S.input, width: '100%', fontWeight: 700, height: '23px', fontSize: '0.72rem' }}
                >
                  <option value="0">0. Piutang</option>
                  <option value="1">1. Tunai</option>
                </select>
              </div>

              {/* TANGGAL VALIDASI */}
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                TANGGAL VALIDASI
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={periode1}
                  disabled={semuaTgl}
                  onChange={e => setPeriode1(e.target.value)}
                  style={{ ...S.input, width: '120px', opacity: semuaTgl ? 0.6 : 1, height: '23px', fontSize: '0.72rem' }}
                />
                <span style={{
                  background: '#004080', color: T.white, padding: '1px 5px',
                  borderRadius: '3px', fontSize: '0.65rem', fontWeight: 800
                }}>
                  S/D
                </span>
                <input
                  type="date"
                  value={periode2}
                  disabled={semuaTgl}
                  onChange={e => setPeriode2(e.target.value)}
                  style={{ ...S.input, width: '120px', opacity: semuaTgl ? 0.6 : 1, height: '23px', fontSize: '0.72rem' }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: T.slate700, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={semuaTgl}
                    onChange={e => setSemuaTgl(e.target.checked)}
                    style={{ cursor: 'pointer', width: '13px', height: '13px' }}
                  />
                  SEMUA TGL
                </label>
              </div>

            </div>

            {/* Row 2: SEARCH NOMOR RM & NAMA PASIEN */}
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 1fr', alignItems: 'center', gap: '6px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                CARI NO RM
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Ketik No Medrec (txtrmcari)..."
                  value={txtrmcari}
                  onChange={e => setTxtrmcari(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchValidasi()}
                  style={{ ...S.input, width: '100%', fontFamily: 'monospace', fontWeight: 700 }}
                />
              </div>

              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                CARI NAMA PASIEN
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <input
                  type="text"
                  placeholder="Ketik Nama Pasien (txtnmrmcari)..."
                  value={txtnmrmcari}
                  onChange={e => setTxtnmrmcari(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchValidasi()}
                  style={{ ...S.input, flex: 1, fontWeight: 600 }}
                />
                <button
                  type="button"
                  onClick={fetchValidasi}
                  disabled={isLoading}
                  style={{ ...S.btnPrimary, height: '24px', padding: '0 10px', fontSize: '0.71rem', flexShrink: 0 }}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-search" />}
                  Cari Data
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── TABLE GRID SECTION WITH PAGINATION ── */}
        <div style={{ ...S.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Table Toolbar */}
          <div style={{
            padding: '3px 8px', borderBottom: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px',
            background: T.white, flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ position: 'relative' }}>
                <i className="bi bi-search" style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: T.slate400, fontSize: '0.68rem', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Filter nota, kuitansi, dokter, kasir..."
                  value={searchTable}
                  disabled={listData.length === 0}
                  onChange={e => setSearchTable(e.target.value)}
                  style={{ ...S.input, paddingLeft: '20px', width: '220px', height: '22px', fontSize: '0.7rem' }}
                />
              </div>
              <span style={{ fontSize: '0.69rem', fontWeight: 700, color: T.slate600 }}>
                Terpilih: <span style={{ color: T.primary, fontWeight: 800 }}>{selectedNotas.length}</span> dari {filteredData.length} Baris
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setSelectedNotas(filteredData.map(i => i.no_kuitansi || i.no_nota))}
                disabled={filteredData.length === 0}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: filteredData.length === 0 ? 0.5 : 1 }}
              >
                Pilih Semua
              </button>
              <button
                onClick={() => setSelectedNotas([])}
                disabled={selectedNotas.length === 0}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: selectedNotas.length === 0 ? 0.5 : 1 }}
              >
                Batal Pilih
              </button>
              <button
                onClick={fetchValidasi}
                disabled={isLoading}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: isLoading ? 0.5 : 1 }}
              >
                <i className={`bi bi-arrow-clockwise${isLoading ? ' spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {/* Table Data (Scrollable) */}
          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <Table hover responsive style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', marginBottom: 0, whiteSpace: 'nowrap' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.headerBg, color: T.headerText }}>
                <tr style={{ borderBottom: `1.5px solid ${T.headerBorder}`, textAlign: 'center' }}>
                  <th style={{ width: '30px', padding: '4px 2px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      disabled={filteredData.length === 0}
                      checked={filteredData.length > 0 && selectedNotas.length === filteredData.length}
                      onChange={handleSelectAll}
                      style={{ cursor: filteredData.length > 0 ? 'pointer' : 'default', width: '12px', height: '12px' }}
                    />
                  </th>
                  <th style={{ width: '36px', padding: '4px 2px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>NO</th>
                  <th style={{ width: '140px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>NO KUITANSI</th>
                  <th style={{ width: '110px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>NO NOTA</th>
                  <th style={{ width: '85px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>TGL PULANG</th>
                  <th style={{ width: '80px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>NO MEDREC</th>
                  <th style={{ width: '190px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'left', whiteSpace: 'nowrap' }}>NAMA PASIEN</th>
                  <th style={{ width: '80px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, whiteSpace: 'nowrap' }}>KASIR</th>
                  <th style={{ width: '180px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'left', whiteSpace: 'nowrap' }}>DOKTER</th>
                  <th style={{ width: '110px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'right', background: '#fef2f2', color: '#991b1b', fontWeight: 800, whiteSpace: 'nowrap' }}>TOTAL</th>
                  <th style={{ width: '95px', padding: '4px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: '24px', color: T.slate400 }}>
                      <Spinner animation="border" size="sm" style={{ marginRight: '6px' }} /> Memuat data verifikasi kasir...
                    </td>
                  </tr>
                ) : pagedData.length > 0 ? (
                  pagedData.map((row, idx) => {
                    const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                    const rowKey = row.no_kuitansi || row.no_nota || idx;
                    const isSelected = selectedNotas.includes(rowKey);
                    const isActive = activeRow && (activeRow.no_kuitansi === row.no_kuitansi || activeRow.no_nota === row.no_nota);
                    return (
                      <tr
                        key={rowKey}
                        onClick={() => toggleRowSelect(rowKey, row)}
                        style={{
                          background: isActive ? '#dbeafe' : isSelected ? '#f0fdf4' : idx % 2 === 0 ? T.white : T.slate50,
                          cursor: 'pointer', transition: 'background .1s', height: '24px'
                        }}
                      >
                        <td style={{ textAlign: 'center', padding: '2px 2px', borderRight: `1px solid ${T.slate100}`, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelect(rowKey, row)}
                            style={{ cursor: 'pointer', width: '12px', height: '12px' }}
                          />
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 2px', borderRight: `1px solid ${T.slate100}`, color: T.slate400, fontSize: '0.69rem', whiteSpace: 'nowrap' }}>
                          {rowNumber}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontFamily: 'monospace', fontWeight: 800, color: T.primaryDk, whiteSpace: 'nowrap' }}>
                          {row.no_kuitansi || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontFamily: 'monospace', fontWeight: 600, color: T.slate700, whiteSpace: 'nowrap' }}>
                          {row.no_nota || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, color: T.slate600, whiteSpace: 'nowrap' }}>
                          {row.tgl_pulang || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {row.no_medrec || '-'}
                        </td>
                        <td style={{ padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, fontWeight: 600, color: T.slate800, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.nm_pasien}>
                          {row.nm_pasien || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, color: T.slate600, whiteSpace: 'nowrap' }}>
                          {row.kasir || '-'}
                        </td>
                        <td style={{ padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, color: T.slate700, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.nm_dokter}>
                          {row.nm_dokter || '-'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, fontWeight: 800, color: T.primaryDk, whiteSpace: 'nowrap' }}>
                          {fmtRp(row.total)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '1px 4px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => handleOpenCetakModal(row)}
                            style={{
                              ...S.btnGhost,
                              padding: '0 6px', height: '20px', fontSize: '0.67rem',
                              background: 'linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%)',
                              color: '#15803d', borderColor: '#86efac', fontWeight: 800
                            }}
                            title="Cetak Ulang baris ini"
                          >
                            <i className="bi bi-printer me-1" /> Cetak Ulang
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: '28px 12px', color: T.slate400 }}>
                      <i className="bi bi-inbox" style={{ fontSize: '1.4rem', display: 'block', marginBottom: '4px' }} />
                      <div style={{ fontWeight: 700, fontSize: '0.76rem', color: T.slate700, marginBottom: '1px' }}>
                        Tidak Ada Data Validasi
                      </div>
                      <div style={{ fontSize: '0.68rem', color: T.slate500 }}>
                        Tidak ditemukan data kuitansi berstatus validasi untuk kriteria tanggal ini.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* ── PAGINATION BAR (ALWAYS VISIBLE & COMPACT) ── */}
          <div style={{
            padding: '2px 8px', borderTop: `1px solid ${T.border}`,
            background: T.slate50, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '4px', flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.68rem', color: T.slate600, fontWeight: 600 }}>
                Menampilkan <strong>{filteredData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}</strong> - <strong>{Math.min(currentPage * itemsPerPage, filteredData.length)}</strong> dari <strong>{filteredData.length}</strong> data
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: '4px' }}>
                <span style={{ fontSize: '0.66rem', color: T.slate500 }}>Baris:</span>
                <select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    border: `1px solid ${T.border}`, borderRadius: '3px',
                    fontSize: '0.68rem', padding: '0 2px', height: '18px', background: T.white, color: T.slate700
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Page Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || filteredData.length === 0}
                title="Halaman Pertama"
                style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === 1 || filteredData.length === 0 ? 0.4 : 1 }}
              >
                <i className="bi bi-chevron-double-left" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || filteredData.length === 0}
                title="Sebelumnya"
                style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === 1 || filteredData.length === 0 ? 0.4 : 1 }}
              >
                <i className="bi bi-chevron-left" />
              </button>

              {renderPaginationButtons()}

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || filteredData.length === 0}
                title="Selanjutnya"
                style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === totalPages || filteredData.length === 0 ? 0.4 : 1 }}
              >
                <i className="bi bi-chevron-right" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || filteredData.length === 0}
                title="Halaman Terakhir"
                style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === totalPages || filteredData.length === 0 ? 0.4 : 1 }}
              >
                <i className="bi bi-chevron-double-right" />
              </button>
            </div>
          </div>
        </div>

        {/* ── BOTTOM PREVIEW & ACTION BAR ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0 }}>

          {/* TOTAL Box (Blue Bar + Black Screen Green Number) */}
          <div style={{
            display: 'flex', alignItems: 'stretch',
            background: '#004080', borderRadius: '3px', overflow: 'hidden', height: '24px'
          }}>
            <div style={{
              color: T.white, fontWeight: 800, fontSize: '0.72rem',
              padding: '0 10px', display: 'flex', alignItems: 'center', flex: 1, letterSpacing: '0.4px'
            }}>
              TOTAL
            </div>
            <div style={{
              background: '#000000', color: '#22c55e', fontWeight: 900,
              fontSize: '0.84rem', padding: '0 12px', minWidth: '150px',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: 'monospace'
            }}>
              {fmtRp(totalSum)}
            </div>
          </div>

          {/* Row 2: Action Buttons Row */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px', marginTop: '1px' }}>
            <button
              onClick={handleOpenCetakModal}
              disabled={listData.length === 0}
              style={{
                ...S.btnGhost,
                background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                border: `1.5px solid #cbd5e1`,
                height: '24px', padding: '0 12px', fontSize: '0.71rem', fontWeight: 800,
                opacity: listData.length === 0 ? 0.6 : 1
              }}
            >
              <i className="bi bi-printer me-1" /> Cetak Ulang
            </button>

            <button
              onClick={() => navigate('/home')}
              style={{
                ...S.btnDanger,
                background: 'linear-gradient(180deg, #fee2e2 0%, #fca5a5 100%)',
                border: '1px solid #ef4444', color: '#991b1b',
                height: '24px', padding: '0 12px', fontSize: '0.71rem', fontWeight: 800
              }}
            >
              <i className="bi bi-x-circle-fill me-1" /> Keluar
            </button>
          </div>

        </div>

      </div>

      {/* ── MODAL DIALOG: VALIDASI DATA JAMINAN KUITANSI DAN KELENGKAPAN DATA PERUJUK (SESUAI GAMBAR) ── */}
      <Modal show={showCetakModal} onHide={() => setShowCetakModal(false)} size="lg" centered>
        <div style={{
          background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden',
          border: '2px solid #004080', boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}>
          {/* Header Title Bar */}
          <div style={{
            background: 'linear-gradient(90deg, #002b49 0%, #004080 100%)',
            color: T.white, padding: '5px 12px', textAlign: 'center',
            fontWeight: 800, fontSize: '0.76rem', letterSpacing: '0.4px'
          }}>
            VALIDASI DATA JAMINAN KUITANSI DAN KELENGKAPAN DATA PERUJUK
          </div>

          <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: '3px', background: '#f1f5f9' }}>

            {/* Row 1: JENIS VALIDASI KASIR */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>JENIS VALIDASI KASIR</div>
              <select
                value={dlgJenisValidasi}
                onChange={e => {
                  const val = e.target.value;
                  setDlgJenisValidasi(val);
                  if (dlgNoKuitansi.trim()) {
                    cekdatapiutang(dlgNoKuitansi, val === 'Tunai' ? '1' : '0');
                  }
                }}
                style={{ ...S.dlgInput, fontWeight: 700 }}
              >
                <option value="Tunai">Tunai</option>
                <option value="Piutang">Piutang</option>
              </select>
            </div>

            {/* Row 2: NOMOR KUITANSI | TANGGAL | NILAI [Rp] */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 120px 140px', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>
                NOMOR KUITANSI | TANGGAL | NILAI [Rp]
                {isLoadingCek && <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px', marginLeft: '6px' }} />}
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  value={dlgNoKuitansi}
                  onChange={e => setDlgNoKuitansi(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && cekdatapiutang()}
                  placeholder="Ketik No Kuitansi lalu Enter..."
                  style={{ ...S.dlgInput, fontFamily: 'monospace', fontWeight: 800, paddingRight: '22px' }}
                />
                <i
                  className={`bi ${isLoadingCek ? 'bi-hourglass-split' : 'bi-search'}`}
                  onClick={() => cekdatapiutang()}
                  title="Cek data piutang (Enter)"
                  style={{
                    position: 'absolute', right: '6px', cursor: 'pointer',
                    fontSize: '0.72rem', color: T.primaryDk
                  }}
                />
              </div>
              <input
                type="text"
                value={dlgTglKuitansi}
                onChange={e => setDlgTglKuitansi(e.target.value)}
                placeholder="Tgl..."
                style={{ ...S.dlgInput, textAlign: 'center' }}
              />
              <div style={{
                background: '#000000', color: '#22c55e', fontWeight: 900,
                fontSize: '0.78rem', padding: '0 8px', height: '23px', borderRadius: '2px',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: 'monospace'
              }}>
                {fmtRp(dlgNilai)}
              </div>
            </div>

            {/* Row 3: NAMA PASIEN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>NAMA PASIEN</div>
              <input
                type="text"
                value={dlgNoMedrec}
                onChange={e => setDlgNoMedrec(e.target.value)}
                placeholder="No RM..."
                style={{ ...S.dlgInput, fontFamily: 'monospace', fontWeight: 700 }}
              />
              <input
                type="text"
                value={dlgNmPasien}
                onChange={e => setDlgNmPasien(e.target.value)}
                placeholder="Nama Pasien..."
                style={{ ...S.dlgInput, fontWeight: 700 }}
              />
            </div>

            {/* Row 4: TIPE JAMINAN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>TIPE JAMIANAN</div>
              <select
                value={dlgTipeJaminan}
                onChange={e => setDlgTipeJaminan(e.target.value)}
                style={{ ...S.dlgInput, fontWeight: 600 }}
              >
                <option value="1. External Perusahaan Rekanan">1. External Perusahaan Rekanan</option>
                <option value="2. Internal Dokter Rumah Sakit">2. Internal Dokter Rumah Sakit</option>
                <option value="3. Internal Pegawai">3. Internal Pegawai</option>
                <option value="BPJS">BPJS</option>
                <option value="Tunai">Tunai</option>
              </select>
            </div>

            {/* Row 5: NAMA PERUSAHAAN PENJAMIN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>NAMA PERUSAHAAN PENJAMIN</div>
              <input
                type="text"
                value={dlgKdPrs}
                onChange={e => setDlgKdPrs(e.target.value)}
                placeholder="Kode Prs..."
                style={{ ...S.dlgInput, fontFamily: 'monospace', fontWeight: 700 }}
              />
              <input
                type="text"
                value={dlgNmPrs}
                onChange={e => setDlgNmPrs(e.target.value)}
                placeholder="Nama Perusahaan Penjamin..."
                style={{ ...S.dlgInput, fontWeight: 600 }}
              />
            </div>

            {/* Row 6: ICD X [DIAGNOSA] */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>ICD X [DIAGNOSA]</div>
              <input
                type="text"
                value={dlgKdIcd}
                onChange={e => setDlgKdIcd(e.target.value)}
                placeholder="Kode ICD..."
                style={{ ...S.dlgInput, fontFamily: 'monospace', fontWeight: 700 }}
              />
              <input
                type="text"
                value={dlgNmIcd}
                onChange={e => setDlgNmIcd(e.target.value)}
                placeholder="Diagnosa Pasien..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 7: TINDAKAN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>TINDAKAN</div>
              <input
                type="text"
                value={dlgTindakan}
                onChange={e => setDlgTindakan(e.target.value)}
                placeholder="Nama Tindakan / Prosedur..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 8: NAMA PERUJUK */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>NAMA PERUJUK</div>
              <input
                type="text"
                value={dlgKdPerujuk}
                onChange={e => setDlgKdPerujuk(e.target.value)}
                placeholder="Kode..."
                style={{ ...S.dlgInput, fontFamily: 'monospace' }}
              />
              <input
                type="text"
                value={dlgNmPerujuk}
                onChange={e => setDlgNmPerujuk(e.target.value)}
                placeholder="Nama Faskes / Dokter Perujuk..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 9: DOKTER PENANGGUNG JAWAB */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>DOKTER PENANGGUNG JAWAB</div>
              <input
                type="text"
                value={dlgKdDokter}
                onChange={e => setDlgKdDokter(e.target.value)}
                placeholder="Kode DPJP..."
                style={{ ...S.dlgInput, fontFamily: 'monospace' }}
              />
              <input
                type="text"
                value={dlgNmDokter}
                onChange={e => setDlgNmDokter(e.target.value)}
                placeholder="Nama Dokter Penanggung Jawab..."
                style={{ ...S.dlgInput, fontWeight: 600 }}
              />
            </div>

            {/* Row 10: NAMA PENJAMIN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>NAMA PENJAMIN</div>
              <input
                type="text"
                value={dlgNmPenjamin}
                onChange={e => setDlgNmPenjamin(e.target.value)}
                placeholder="Nama Penjamin..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 11: TEMPAT BEKERJA / DIVISI */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>TEMPAT BEKERJA / DIVISI</div>
              <input
                type="text"
                value={dlgTempatBekerja}
                onChange={e => setDlgTempatBekerja(e.target.value)}
                placeholder="Instansi / Unit Kerja / Divisi..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 12: SEP | KELAS RAWAT */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 140px', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>SEP | KELAS RAWAT</div>
              <input
                type="text"
                value={dlgNoSep}
                onChange={e => setDlgNoSep(e.target.value)}
                placeholder="Nomor SEP BPJS..."
                style={S.dlgInput}
              />
              <input
                type="text"
                value={dlgKelasRawat}
                onChange={e => setDlgKelasRawat(e.target.value)}
                placeholder="Kelas Rawat..."
                style={S.dlgInput}
              />
            </div>

            {/* Row 13: PAKET RUJUKAN | NILAI RUJUKAN */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 110px 1fr 140px', alignItems: 'center', gap: '4px' }}>
              <div style={S.dlgLabel}>PAKET RUJUKAN | NILAI RUJUKAN</div>
              <input
                type="text"
                value={dlgKdPaket}
                onChange={e => setDlgKdPaket(e.target.value)}
                placeholder="Kode Paket..."
                style={{ ...S.dlgInput, fontFamily: 'monospace' }}
              />
              <input
                type="text"
                placeholder="Nama Paket Rujukan..."
                style={S.dlgInput}
              />
              <input
                type="text"
                value={dlgNilaiRujukan}
                onChange={e => setDlgNilaiRujukan(e.target.value)}
                placeholder="Nilai Rujukan (Rp)..."
                style={{ ...S.dlgInput, textAlign: 'right', fontFamily: 'monospace' }}
              />
            </div>

            {/* Bottom Dialog Action Buttons Row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '4px', flexWrap: 'wrap', marginTop: '6px', paddingTop: '6px',
              borderTop: '1px solid #cbd5e1'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    showToast('Data validasi kuitansi berhasil disimpan!', 'success');
                  }}
                  style={S.dlgBtn}
                >
                  <i className="bi bi-floppy" /> <u>S</u>IMPAN
                </button>

                <button
                  type="button"
                  onClick={handleCetakHasilLab}
                  style={S.dlgBtn}
                  title="Cetak Hasil Laboratorium Gabungan (cetakhasillab_all)"
                >
                  <i className="bi bi-prescription2" /> HASIL LAB
                </button>

                <button
                  type="button"
                  onClick={handleCetakHasilRad}
                  style={S.dlgBtn}
                  title="Cetak Hasil Radiologi Gabungan (cetakhasilrad_gab)"
                >
                  <i className="bi bi-radioactive" /> HASIL RAD
                </button>

                <button
                  type="button"
                  onClick={handleCetakUlangJaminan}
                  style={S.dlgBtn}
                  title="Cetak Ulang Kuitansi & Rincian Tagihan"
                >
                  <i className="bi bi-printer" /> Cetak Ulang
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setDlgJenisValidasi('Tunai');
                    showToast('Dipindahkan ke status Tunai.');
                  }}
                  style={{ ...S.dlgBtn, opacity: 0.85 }}
                >
                  <i className="bi bi-arrow-left-right" /> PINDAH KE TUNAI
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowCetakCariDataModal(true);
                  }}
                  style={S.dlgBtn}
                >
                  <i className="bi bi-binoculars" /> Laporan Transaksi Piutang
                </button>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowCetakModal(false)}
                  style={{
                    ...S.dlgBtn,
                    background: 'linear-gradient(180deg, #fee2e2 0%, #fca5a5 100%)',
                    border: '1px solid #ef4444', color: '#991b1b'
                  }}
                >
                  <i className="bi bi-x-circle-fill" /> Keluar
                </button>
              </div>
            </div>

          </div>
        </div>
      </Modal>

      {/* ── Modal Preview PDF Cetak Hasil Lab / Rad ── */}
      <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} size="xl" centered backdrop="static">
        <Modal.Header closeButton style={{ background: '#003366', color: '#ffffff', padding: '8px 16px' }}>
          <Modal.Title style={{ fontSize: '0.88rem', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="bi bi-file-earmark-pdf-fill" style={{ color: '#fca5a5' }} />
            {pdfModalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '75vh', backgroundColor: '#334155' }}>
          {pdfPreviewUrl && (
            <iframe
              id="previewPdfIframe"
              src={pdfPreviewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Preview PDF Dokumen"
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 14px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowPdfModal(false)} style={{ fontSize: '0.74rem' }}>
            <i className="bi bi-x me-1" /> Tutup
          </Button>
          <div style={{ display: 'flex', gap: '6px' }}>
            <a
              href={pdfPreviewUrl}
              download={pdfDownloadName}
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: '0.74rem', fontWeight: 600 }}
            >
              <i className="bi bi-download" /> Download PDF
            </a>
            <Button
              variant="primary"
              size="sm"
              style={{ background: '#0d9488', borderColor: '#0d9488', fontSize: '0.74rem', fontWeight: 700 }}
              onClick={() => {
                const iframe = document.getElementById('previewPdfIframe');
                if (iframe) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
              }}
            >
              <i className="bi bi-printer-fill me-1" /> Cetak Dokumen
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Konfirmasi Cetak Ulang Nota Lab ── */}
      <Modal show={showConfirmLabModal} onHide={() => setShowConfirmLabModal(false)} centered backdrop="static" size="sm">
        <div style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
          <div style={{ background: '#003366', color: '#ffffff', padding: '8px 12px', fontWeight: 800, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="bi bi-question-circle-fill text-warning" /> Konfirmasi Cetak Ulang
          </div>
          <div style={{ padding: '16px 14px', textAlign: 'center' }}>
            <i className="bi bi-prescription2" style={{ fontSize: '2.4rem', color: '#0f766e', display: 'block', marginBottom: '8px' }} />
            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
              Yakin akan cetak ulang nota laboratorium?
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
              No Kuitansi: <strong>{dlgNoKuitansi || '-'}</strong> {dlgNmPasien && `| ${dlgNmPasien}`}
            </div>
          </div>
          <div style={{ background: '#f8fafc', padding: '8px 12px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowConfirmLabModal(false)}
              style={{
                ...S.btnGhost,
                padding: '3px 12px', fontSize: '0.74rem', fontWeight: 600, height: '26px',
                background: '#ffffff', borderColor: '#cbd5e1', color: '#475569'
              }}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={executeCetakHasilLab}
              style={{
                ...S.btnGhost,
                padding: '3px 14px', fontSize: '0.74rem', fontWeight: 700, height: '26px',
                background: 'linear-gradient(180deg, #0d9488 0%, #0f766e 100%)',
                color: '#ffffff', borderColor: '#0f766e'
              }}
            >
              <i className="bi bi-printer me-1" /> Ya, Cetak
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Modal Progress Bar ── */}
      <Modal show={showProgressModal} centered backdrop="static" size="sm">
        <div style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', padding: '16px 18px', border: '1px solid #cbd5e1', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Spinner animation="border" size="sm" style={{ color: '#0d9488', width: '16px', height: '16px' }} />
            <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b' }}>
              Memproses Dokumen...
            </div>
          </div>
          <ProgressBar
            now={progressPercent}
            label={`${progressPercent}%`}
            animated
            variant="info"
            style={{ height: '18px', fontSize: '0.7rem', fontWeight: 800, borderRadius: '4px' }}
          />
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '8px', textAlign: 'center', fontWeight: 600 }}>
            {progressStatusText}
          </div>
        </div>
      </Modal>
      {/* Modal Cetak Data Transaksi Piutang */}
      <Modal
        show={showCetakCariDataModal}
        onHide={() => setShowCetakCariDataModal(false)}
        centered
        backdrop="static"
        dialogClassName="modal-md"
      >
        <div style={{ backgroundColor: '#2d2d2d', borderRadius: '4px', padding: '2px', color: '#fff', border: '1px solid #000' }}>
          <div style={{
            background: '#002b3d',
            padding: '6px', textAlign: 'center', fontWeight: 'bold', fontSize: '13px',
            border: '1px solid #001a26', marginBottom: '12px', color: '#fff'
          }}>
            CETAK DATA TRANSAKSI PIUTANG
          </div>
          <div style={{ padding: '8px 16px 16px 16px' }}>
            
            {/* Opsi 1 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input 
                  type="radio" 
                  name="cetakCariType" 
                  checked={cetakCariType === '1'} 
                  onChange={() => setCetakCariType('1')}
                  style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff' }}>LAPORAN PEKERJAAN TELAH SELESAI</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '24px' }}>
                <div style={{ background: '#003380', color: '#fff', padding: '2px 0', fontSize: '11px', fontWeight: 'bold', width: '80px', textAlign: 'center', border: '1px solid #001f4d' }}>
                  PERIODE
                </div>
                <input 
                  type="date" 
                  value={cetakCariTgl1} 
                  onChange={e => setCetakCariTgl1(e.target.value)}
                  disabled={cetakCariType !== '1'}
                  style={{ flex: 1, height: '22px', fontSize: '11px', padding: '0 4px', border: 'none', color: '#000', fontWeight: 'bold' }} 
                />
                <div style={{ background: '#003380', color: '#fff', padding: '2px 0', fontSize: '11px', fontWeight: 'bold', width: '40px', textAlign: 'center', border: '1px solid #001f4d' }}>
                  S.d
                </div>
                <input 
                  type="date" 
                  value={cetakCariTgl2} 
                  onChange={e => setCetakCariTgl2(e.target.value)}
                  disabled={cetakCariType !== '1'}
                  style={{ flex: 1, height: '22px', fontSize: '11px', padding: '0 4px', border: 'none', color: '#000', fontWeight: 'bold' }} 
                />
              </div>
            </div>

            {/* Opsi 2 */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input 
                  type="radio" 
                  name="cetakCariType" 
                  checked={cetakCariType === '2'} 
                  onChange={() => setCetakCariType('2')}
                  style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff', border: cetakCariType === '2' ? '1px dotted #ccc' : 'none', padding: '1px 2px' }}>
                  LAPORAN PEKERJAAN BELUM SELESAI [SEMUA TANGGAL]
                </span>
              </div>

            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button 
                onClick={handleCetakLaporan}
                style={{ ...S.dlgBtn, height: '24px', fontSize: '0.75rem', padding: '0 12px' }}
              >
                <i className="bi bi-printer" /> Cetak
              </button>
              <button 
                onClick={() => setShowCetakCariDataModal(false)}
                style={{ ...S.dlgBtn, height: '24px', fontSize: '0.75rem', padding: '0 12px', background: 'linear-gradient(180deg, #fee2e2 0%, #fca5a5 100%)', color: '#991b1b', border: '1px solid #ef4444' }}
              >
                <i className="bi bi-x-circle-fill" /> Tutup
              </button>
            </div>
          </div>
        </div>
      </Modal>


      {/* ── Modal Konfirmasi Cetak Ulang Validasi Jaminan ── */}
      <Modal show={showConfirmCetakUlangModal} onHide={() => setShowConfirmCetakUlangModal(false)} centered backdrop="static" size="sm">
        <div style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
          <div style={{ background: '#003366', color: '#ffffff', padding: '8px 12px', fontWeight: 800, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="bi bi-question-circle-fill text-warning" /> Konfirmasi Cetak
          </div>
          <div style={{ padding: '16px 14px', textAlign: 'center' }}>
            <i className="bi bi-printer-fill" style={{ fontSize: '2.4rem', color: '#0f766e', display: 'block', marginBottom: '8px' }} />
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
              Yakin akan di cetak
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
              No Kuitansi: <strong>{dlgNoKuitansi || '-'}</strong> {dlgNmPasien && `| ${dlgNmPasien}`}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#0f766e', fontWeight: 600, marginTop: '2px' }}>
              Penjamin: {dlgNmPrs || '-'}
            </div>
          </div>
          <div style={{ background: '#f8fafc', padding: '8px 12px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowConfirmCetakUlangModal(false)}
              style={{
                ...S.btnGhost,
                padding: '3px 12px', fontSize: '0.74rem', fontWeight: 600, height: '26px',
                background: '#ffffff', borderColor: '#cbd5e1', color: '#475569'
              }}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={executeCetakUlangJaminan}
              style={{
                ...S.btnGhost,
                padding: '3px 14px', fontSize: '0.74rem', fontWeight: 700, height: '26px',
                background: 'linear-gradient(180deg, #0d9488 0%, #0f766e 100%)',
                color: '#ffffff', borderColor: '#0f766e'
              }}
            >
              <i className="bi bi-printer me-1" /> Ya
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .spin { animation: spin .8s linear infinite; }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ValidasiRekananPerusahaan;
